import { supabase } from '../lib/supabase';

export interface SiteContent {
  id: string;
  key: string;
  title: string;
  content: string;
  content_type: string;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface SiteContentUpdate {
  title?: string;
  content?: string;
  content_type?: string;
}

export interface SingAlongBranding {
  logo_url: string | null;
  sponsor_logos: string[];
  sponsor_links: string[];
  primary_color: string;
  accent_color: string;
}

export const SiteContentService = {
  async getContentByKey(key: string): Promise<SiteContent | null> {
    const { data, error } = await supabase
      .from('site_content')
      .select('*')
      .eq('key', key)
      .maybeSingle();

    if (error) {
      console.error('Error fetching site content:', error);
      throw error;
    }

    return data;
  },

  async getAllContent(): Promise<SiteContent[]> {
    const { data, error } = await supabase
      .from('site_content')
      .select('*')
      .order('key');

    if (error) {
      console.error('Error fetching all site content:', error);
      throw error;
    }

    return data || [];
  },

  async updateContent(key: string, updates: SiteContentUpdate): Promise<SiteContent> {
    const { data, error } = await supabase
      .from('site_content')
      .update(updates)
      .eq('key', key)
      .select()
      .single();

    if (error) {
      console.error('Error updating site content:', error);
      throw error;
    }

    return data;
  },

  async createContent(content: Omit<SiteContent, 'id' | 'created_at' | 'updated_at' | 'updated_by'>): Promise<SiteContent> {
    const { data, error } = await supabase
      .from('site_content')
      .insert(content)
      .select()
      .single();

    if (error) {
      console.error('Error creating site content:', error);
      throw error;
    }

    return data;
  },

  async deleteContent(key: string): Promise<void> {
    const { error } = await supabase
      .from('site_content')
      .delete()
      .eq('key', key);

    if (error) {
      console.error('Error deleting site content:', error);
      throw error;
    }
  },

  async getSingAlongBranding(): Promise<SingAlongBranding> {
    const defaults: SingAlongBranding = {
      logo_url: null,
      sponsor_logos: [],
      sponsor_links: [],
      primary_color: '#f97316',
      accent_color: '#f59e0b',
    };

    const row = await this.getContentByKey('singalong_branding');
    if (!row) return defaults;

    try {
      const parsed = JSON.parse(row.content);
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  },

  async saveSingAlongBranding(branding: SingAlongBranding): Promise<void> {
    const content = JSON.stringify(branding);
    const existing = await this.getContentByKey('singalong_branding');

    if (existing) {
      await this.updateContent('singalong_branding', { content });
    } else {
      await this.createContent({
        key: 'singalong_branding',
        title: 'Email Branding',
        content,
        content_type: 'json',
      });
    }
  },

  async uploadSingAlongAsset(file: File, kind: string): Promise<string> {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const path = `singalong-global/${kind}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from('kiosk_assets')
      .upload(path, file, { contentType: file.type || 'image/png', upsert: true });
    if (error) throw new Error(error.message);
    const { data } = supabase.storage.from('kiosk_assets').getPublicUrl(path);
    return data.publicUrl;
  }
};
